// Live results for the offcanvas site search. Fetches the states_search JSON
// endpoint (debounced) and renders result cards into the panel.

interface SearchResultItem {
  title: string;
  url: string;
  excerpt: string; // server-sanitized HTML (only <strong>/<em>/<b>/<i>)
  type: string;
  is_video: boolean;
  state: string;
  color: string | null;
  thumbnail: string;
  thumbnail_srcset: string;
  thumbnail_sizes: string;
}

interface SearchResponse {
  query: string;
  total: number;
  results: SearchResultItem[];
}

const DEBOUNCE_MS = 250;
// Matches the states_search index's tokenizer/backend minimum word length —
// below that the backend can't match anything, so don't bother querying.
const MIN_LENGTH = 3;

const STAR_SVG =
  '<svg class="search-result__star" viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" focusable="false"><polygon points="10,1 12.35,7.27 19,7.64 14,12.14 15.59,18.9 10,15.4 4.41,18.9 6,12.14 1,7.64 7.65,7.27"/></svg>';
const PLAY_SVG =
  '<svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M4 3l10 5-10 5V3z" fill="currentColor"/></svg>';

export interface SearchResultsController {
  /** Clears the field, results and any pending request (called on panel close). */
  reset(): void;
}

export function initSearchResults(root: HTMLElement): SearchResultsController | null {
  const form = root.querySelector<HTMLFormElement>('.js-search-form');
  const input = root.querySelector<HTMLInputElement>('.js-search-input');
  const clearBtn = root.querySelector<HTMLButtonElement>('.js-search-clear');
  const resultsEl = root.querySelector<HTMLElement>('.js-search-results');
  const endpoint = root.dataset.resultsUrl;

  if (!form || !input || !resultsEl || !endpoint) return null;

  // Non-null locals so the closures below don't need repeated guards.
  const inputEl = input;
  const results = resultsEl;

  let debounceId: number | undefined;
  let controller: AbortController | undefined;

  const setClearVisible = (visible: boolean): void => {
    if (clearBtn) clearBtn.hidden = !visible;
  };

  const setExpanded = (expanded: boolean): void => {
    inputEl.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  };

  function clearResults(): void {
    results.replaceChildren();
    setExpanded(false);
  }

  function withHighlight(url: string, query: string): string {
    try {
      const target = new URL(url, window.location.origin);
      target.searchParams.set('highlight', query);
      return target.pathname + target.search + target.hash;
    } catch {
      return url;
    }
  }

  function buildCard(item: SearchResultItem, query: string): HTMLElement {
    const card = document.createElement('a');
    card.className = 'search-result';
    card.href = withHighlight(item.url, query);
    card.setAttribute('role', 'option');

    const body = document.createElement('div');
    body.className = 'search-result__body';

    if (item.state) {
      const pill = document.createElement('span');
      pill.className = 'search-result__pill';
      if (item.color) pill.style.setProperty('--state-color', item.color);
      pill.innerHTML = STAR_SVG; // static markup
      const name = document.createElement('span');
      name.textContent = item.state; // entity label — text only
      pill.append(name);
      body.append(pill);
    }

    const title = document.createElement('h3');
    title.className = 'search-result__title';
    title.textContent = item.title;
    body.append(title);

    if (item.excerpt) {
      const excerpt = document.createElement('p');
      excerpt.className = 'search-result__excerpt';
      excerpt.innerHTML = item.excerpt; // sanitized server-side
      body.append(excerpt);
    }

    card.append(body);

    if (item.thumbnail) {
      const media = document.createElement('div');
      media.className = 'search-result__media';
      const img = document.createElement('img');
      img.src = item.thumbnail;
      if (item.thumbnail_srcset) {
        img.srcset = item.thumbnail_srcset;
        img.sizes = item.thumbnail_sizes;
      }
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      media.append(img);
      if (item.is_video) {
        const play = document.createElement('span');
        play.className = 'search-result__play';
        play.setAttribute('aria-hidden', 'true');
        play.innerHTML = PLAY_SVG;
        media.append(play);
      }
      card.append(media);
    }

    return card;
  }

  function renderResults(data: SearchResponse): void {
    results.replaceChildren();

    if (!data.results.length) {
      const empty = document.createElement('p');
      empty.className = 'search__empty';
      empty.textContent = data.query ? `No results for “${data.query}”.` : '';
      results.append(empty);
      setExpanded(false);
      return;
    }

    // Cap the stagger index so rapid re-renders never accumulate a long
    // delay tail. Cards beyond MAX_STAGGER_INDEX share the same delay as
    // the last staggered card and still animate in together.
    const MAX_STAGGER_INDEX = 5;

    const frag = document.createDocumentFragment();
    data.results.forEach((item, index) => {
      const card = buildCard(item, data.query);
      card.style.setProperty('--i', String(Math.min(index, MAX_STAGGER_INDEX)));
      frag.append(card);
    });
    results.append(frag);
    setExpanded(true);
  }

  async function runSearch(query: string): Promise<void> {
    controller?.abort();
    controller = new AbortController();
    try {
      const res = await fetch(`${endpoint}?q=${encodeURIComponent(query)}`, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`Search request failed: ${res.status}`);
      renderResults((await res.json()) as SearchResponse);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      results.replaceChildren();
      const fail = document.createElement('p');
      fail.className = 'search__empty';
      fail.textContent = 'Something went wrong. Please try again.';
      results.append(fail);
    }
  }

  function handleQuery(value: string, immediate = false): void {
    window.clearTimeout(debounceId);
    const trimmed = value.trim();
    setClearVisible(trimmed.length > 0);

    if (trimmed.length < MIN_LENGTH) {
      controller?.abort();
      clearResults();
      return;
    }

    if (immediate) {
      void runSearch(trimmed);
    } else {
      debounceId = window.setTimeout(() => void runSearch(trimmed), DEBOUNCE_MS);
    }
  }

  inputEl.addEventListener('input', () => handleQuery(inputEl.value));

  form.addEventListener('submit', (e: SubmitEvent) => {
    e.preventDefault();
    handleQuery(inputEl.value, true);
  });

  clearBtn?.addEventListener('click', () => {
    inputEl.value = '';
    setClearVisible(false);
    clearResults();
    inputEl.focus();
  });

  return {
    reset(): void {
      window.clearTimeout(debounceId);
      controller?.abort();
      inputEl.value = '';
      setClearVisible(false);
      clearResults();
    },
  };
}
